import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { ISpClaimedReward } from '@brushfire/types'
import { RiCalendarEventLine } from '@/shared/icons/remix'
import { REWARD_PROCESSING_VIEW_TOOLTIP } from '../lib/rewards-copy'
import {
  claimedRewardAmountPillSx,
  claimedRewardAmountTextSx,
  claimedRewardBadgesRowSx,
  claimedRewardBrandNameSx,
  claimedRewardBrandRowSx,
  claimedRewardCardSx,
  claimedRewardDateRowSx,
  claimedRewardDateTextSx,
  claimedRewardLogoPlaceholderSx,
  claimedRewardLogoSx,
  claimedRewardStatusProcessingSx,
  claimedRewardStatusProcessingTextSx,
  claimedRewardStatusFailedSx,
  claimedRewardStatusFailedTextSx,
  claimedRewardStatusReadySx,
  claimedRewardStatusReadyTextSx,
  claimedRewardViewButtonSx,
} from '../styles/claimedRewardsStyles'
import { ClaimedRewardViewModal } from './ClaimedRewardViewModal'
import { BrandLogoImage } from './BrandLogoImage'

interface Props {
  reward: ISpClaimedReward
  autoOpen?: boolean
}

function formatRedeemedDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

function formatClaimedAmount(amountUsd: number): string {
  return `$ ${amountUsd.toFixed(2)}`
}

interface ViewButtonParams {
  isProcessing: boolean
  canView: boolean
  rewardId: string
  viewButtonSx: object
  onView: () => void
}

function renderViewButton({
  isProcessing,
  canView,
  rewardId,
  viewButtonSx,
  onView,
}: ViewButtonParams) {
  if (isProcessing) {
    return (
      <Tooltip title={REWARD_PROCESSING_VIEW_TOOLTIP} describeChild>
        <span>
          <Button
            variant="contained"
            disabled
            data-testid={`rewards-claimed-view-${rewardId}`}
            sx={viewButtonSx}
          >
            View
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <Button
      variant="contained"
      disabled={!canView}
      onClick={onView}
      data-testid={`rewards-claimed-view-${rewardId}`}
      sx={viewButtonSx}
    >
      View
    </Button>
  )
}

export function ClaimedRewardCard({ reward, autoOpen = false }: Readonly<Props>) {
  const [viewOpen, setViewOpen] = useState(false)
  const isReady = reward.status === 'ready'
  const isProcessing = reward.status === 'processing'
  const isFailed = reward.status === 'failed'
  const canView = isReady || isFailed

  useEffect(() => {
    if (autoOpen && canView) {
      setViewOpen(true)
    }
  }, [autoOpen, canView])

  return (
    <>
      <Box sx={claimedRewardCardSx} data-testid={`rewards-claimed-row-${reward.id}`}>
        <Box sx={claimedRewardBrandRowSx}>
          <BrandLogoImage
            src={reward.brandLogoUrl}
            imageSx={claimedRewardLogoSx}
            placeholderSx={claimedRewardLogoPlaceholderSx}
            testId={`rewards-claimed-logo-${reward.id}`}
          />
          <Typography component="h3" sx={claimedRewardBrandNameSx}>
            {reward.brandName} Gift Card
          </Typography>
        </Box>

        <Box sx={claimedRewardBadgesRowSx}>
          <Box sx={claimedRewardAmountPillSx}>
            <Typography component="span" sx={claimedRewardAmountTextSx}>
              {formatClaimedAmount(reward.amountUsd)}
            </Typography>
          </Box>
          {isReady ? (
            <Box sx={claimedRewardStatusReadySx} data-testid={`rewards-claimed-ready-${reward.id}`}>
              <Typography component="span" sx={claimedRewardStatusReadyTextSx}>
                Ready
              </Typography>
            </Box>
          ) : null}
          {isProcessing ? (
            <Box
              sx={claimedRewardStatusProcessingSx}
              data-testid={`rewards-claimed-processing-${reward.id}`}
            >
              <Typography component="span" sx={claimedRewardStatusProcessingTextSx}>
                Processing
              </Typography>
            </Box>
          ) : null}
          {isFailed ? (
            <Box
              sx={claimedRewardStatusFailedSx}
              data-testid={`rewards-claimed-failed-${reward.id}`}
            >
              <Typography component="span" sx={claimedRewardStatusFailedTextSx}>
                Failed
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box sx={claimedRewardDateRowSx}>
          <RiCalendarEventLine size={18} aria-hidden />
          <Typography component="span" sx={claimedRewardDateTextSx}>
            {formatRedeemedDate(reward.redeemedAt)}
          </Typography>
        </Box>

        {renderViewButton({
          isProcessing,
          canView,
          rewardId: reward.id,
          viewButtonSx: claimedRewardViewButtonSx,
          onView: () => setViewOpen(true),
        })}
      </Box>

      <ClaimedRewardViewModal
        open={viewOpen}
        reward={reward}
        onClose={() => setViewOpen(false)}
      />
    </>
  )
}
